import { faCheck, faChevronRight, faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';

import { ColorVariants } from '@/constants/color';
import useCompactTaskList from '@/hooks/useCompactTaskList';
import useUpdateItem from '@/hooks/useUpdateItem';
import { Button } from '@/ui/Button';
import Spinner from '@/ui/components/Spinner';
import { Tooltip } from '@/ui/Tooltip';
import { Typography } from '@/ui/Typography';
import statusColor from '@/utils/statusColor';
import taskDueDateLabel from '@/utils/taskDueDateLabel';

import type { Props } from './types';

const MAX_VISIBLE = 5;

const CompactTaskList = ({ personId, taskables }: Props) => {
  const { data, isPending, refetch } = useCompactTaskList({ personId, taskables });
  // personId is this list's cache identity, not the task's person: an
  // encounter-attached task can have no person, so useUpdateItem's
  // person-keyed invalidation would miss this list.
  const updateItem = useUpdateItem({ onSuccess: () => refetch() });

  const items = data?.data?.results ?? [];
  const visible = items.slice(0, MAX_VISIBLE);

  return (
    <>
      {isPending ? (
        <div className="mt-2 flex justify-center py-4">
          <Spinner />
        </div>
      ) : visible.length === 0 ? (
        <Typography className="mt-2" muted>
          No open tasks
        </Typography>
      ) : (
        <ul className="mt-2" role="list">
          {visible.map((item) => {
            const due = taskDueDateLabel(item.due_at);
            const itemHref = `/people/${personId}/tasks/${item.id}`;
            const isInProgress = item.status === 'in_progress';

            return (
              <li key={item.id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                <span
                  aria-hidden
                  className={`h-2 w-2 shrink-0 rounded-full ${ColorVariants[statusColor[item.status]]}`}
                />

                <Link to={itemHref} className="group min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-uniform-gray-900 group-hover:underline dark:text-uniform-white-base">
                    {item.name}
                  </p>
                  <p
                    className={`text-xs ${due.overdue ? 'text-uniform-red-700' : 'text-uniform-gray-500'}`}>
                    {due.text}
                  </p>
                </Link>

                <div className="flex shrink-0 items-center gap-1">
                  <Tooltip
                    content={
                      isInProgress ? 'Unclaim and release the work' : 'Claim and mark in progress'
                    }
                    placement="top">
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      color={isInProgress ? 'secondary' : 'neutral'}
                      aria-label={isInProgress ? 'Unclaim task' : 'Start task'}
                      disabled={updateItem.isPending}
                      onClick={() =>
                        updateItem.mutate({
                          id: item.id,
                          values: isInProgress
                            ? { status: 'open', assigned_user_id: null }
                            : { status: 'in_progress' }
                        })
                      }
                      leftIcon={isInProgress ? faPause : faPlay}
                    />
                  </Tooltip>

                  <Tooltip content="Claim and mark complete" placement="top">
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      color="neutral"
                      aria-label="Complete task"
                      disabled={updateItem.isPending}
                      onClick={() =>
                        updateItem.mutate({ id: item.id, values: { status: 'closed' } })
                      }
                      leftIcon={faCheck}
                    />
                  </Tooltip>

                  <Link
                    to={itemHref}
                    aria-label="View task"
                    className="rounded p-1.5 text-uniform-gray-300 hover:text-uniform-gray-500">
                    <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

export default CompactTaskList;
